import { Box, Tooltip, Typography } from "@mui/material";
import { formatNumber } from "../format";
import { LoanBalancePoint, niceMax, toMillions } from "./borrowerSummary";

/**
 * Loan Balance body — Figma "Chart/Loan_Lifecycle" 7846:52192.
 *
 * Fixed Figma geometry (Chart 7846:52450):
 *   8 rows of 16px, 20px apart: the axis captions, then 7 ticks (max → 0). Each
 *   row is a 108px left label, 13px gap, the gridline, 13px gap, a 108px right
 *   label. Columns (62px, bar 40px) are spread over a strip 149px in from each
 *   side, 44px down (the top tick's centre), so a full bar is 216px tall and its
 *   year label sits 7px under it.
 *
 * Principal and outstanding share one column: the principal bar, with the
 * outstanding bar drawn over it from the baseline. Both use the same scale, so
 * the left (principal) and right (outstanding) axes read the same.
 */
const TICKS = 7;
const STEPS = TICKS - 1;
const ROW = 16;
const ROW_GAP = 20;
const SIDE_LABEL = 108;
const SIDE_GAP = 13;
const STRIP_INSET = 149;
const STRIP_TOP = ROW + ROW_GAP + ROW / 2; // 44
const PLOT = STEPS * (ROW + ROW_GAP); // 216
const COLUMN = 62;
const BAR = 40;
const X_LABEL = 24;
const X_LABEL_GAP = 7;

interface Props {
  points: LoanBalancePoint[];
  principalLabel: string;
  outstandingLabel: string;
}

const axisText = {
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "normal",
  color: "var(--chart-axis)",
  width: `${SIDE_LABEL}px`,
  flexShrink: 0,
} as const;

const LoanBalanceChart = ({ points, principalLabel, outstandingLabel }: Props) => {
  const series = points.map((p) => ({
    year: p.year,
    principal: toMillions(p.principal),
    outstanding: toMillions(p.outstanding),
  }));
  const axisMax = niceMax(
    Math.max(0, ...series.flatMap((p) => [p.principal, p.outstanding])),
    STEPS,
  );
  const px = (v: number) => (v / axisMax) * PLOT;

  const rows: { left: string; right: string; grid: boolean }[] = [
    { left: principalLabel, right: outstandingLabel, grid: false },
    ...Array.from({ length: TICKS }, (_, i) => {
      const tick = formatNumber((axisMax * (STEPS - i)) / STEPS);
      return { left: tick, right: tick, grid: true };
    }),
  ];

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Box position="relative" pb="28px">
        <Box display="flex" flexDirection="column" gap={`${ROW_GAP}px`}>
          {rows.map((r, i) => (
            <Box
              key={i}
              height={`${ROW}px`}
              display="flex"
              alignItems="center"
              gap={`${SIDE_GAP}px`}
            >
              <Typography {...axisText} textAlign="right" noWrap>
                {r.left}
              </Typography>
              <Box
                flex={1}
                height="1px"
                bgcolor={r.grid ? "var(--chart-grid)" : "transparent"}
              />
              {/* The right caption is wider than Figma's 108px "(THB)" box once it
                  reads "(M THB)", so it overflows leftwards over the (unlined)
                  caption row instead of being cut off. */}
              <Box
                width={`${SIDE_LABEL}px`}
                flexShrink={0}
                display="flex"
                justifyContent={r.grid ? "flex-start" : "flex-end"}
              >
                <Typography {...axisText} width="auto" whiteSpace="nowrap">
                  {r.right}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Columns 7846:52469 */}
        <Box
          position="absolute"
          left={`${STRIP_INSET}px`}
          right={`${STRIP_INSET}px`}
          top={`${STRIP_TOP}px`}
          height={`${PLOT + X_LABEL_GAP + X_LABEL}px`}
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
        >
          {series.map((p) => (
            <Box
              key={p.year}
              width={`${COLUMN}px`}
              flexShrink={0}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={`${X_LABEL_GAP}px`}
            >
              <Tooltip
                title={`${principalLabel}: ${formatNumber(p.principal)} · ${outstandingLabel}: ${formatNumber(p.outstanding)}`}
              >
                {/* The column clips both bars to its 4px top corners, as Figma's
                    "Colunm Chart" frame does. */}
                <Box
                  position="relative"
                  width={`${BAR}px`}
                  height={`${px(Math.max(p.principal, p.outstanding))}px`}
                  overflow="hidden"
                  borderRadius="4px 4px 0 0"
                >
                  <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={0}
                    height={`${px(p.principal)}px`}
                    bgcolor="var(--chart-principal)"
                  />
                  <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={0}
                    height={`${px(p.outstanding)}px`}
                    bgcolor="var(--chart-outstanding)"
                  />
                </Box>
              </Tooltip>
              <Typography
                width="100%"
                textAlign="center"
                fontSize="14px"
                fontWeight={500}
                lineHeight={`${X_LABEL}px`}
                color="var(--chart-axis)"
              >
                {p.year}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Legend 7846:52270 */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="28px"
        py="2px"
        flexWrap="wrap"
      >
        {[
          { label: principalLabel, color: "var(--chart-principal)" },
          { label: outstandingLabel, color: "var(--chart-outstanding)" },
        ].map((l) => (
          <Box key={l.label} display="flex" alignItems="center" gap="8px">
            <Box width="12px" height="12px" borderRadius="2px" bgcolor={l.color} />
            <Typography fontSize="12px" fontWeight={600} lineHeight="16px" color="var(--dark)">
              {l.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LoanBalanceChart;
